import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Wallet } from "lucide-react";
import { InstallAppButton } from "@/components/app/InstallAppButton";
import Captcha from "@/components/app/Captcha";
import { AuthFooter } from "@/components/app/AuthFooter";
import { ResendVerification } from "@/components/app/ResendVerification";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [needVerify, setNeedVerify] = useState("");
  const [module, setModule] = useState(() => { try { return localStorage.getItem("khn_module") === "pribadi" ? "pribadi" : "bisnis"; } catch { return "bisnis"; } });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password, captchaToken, module);
    setLoading(false);
    if (r.ok) { toast.success("Selamat datang!"); nav("/"); }
    else { toast.error(r.error); setNeedVerify(/belum verifikasi/i.test(r.error || "") ? email : ""); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img
          src="https://images.pexels.com/photos/32327756/pexels-photo-32327756.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Skyline"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/60"/>
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <div className="flex items-center gap-2">
            <Wallet size={22} className="text-emerald-400"/>
            <span className="font-semibold tracking-tight">Pusat Keuangan</span>
          </div>
          <div>
            <h2 className="text-4xl font-medium tracking-tight max-w-md">Kelola pembukuan & pajak dalam satu tempat.</h2>
            <p className="text-slate-200 mt-3 max-w-md">Pemasukan, pengeluaran, PPh 0,5%, dan tarif normal 22% terhitung otomatis.</p>
          </div>
        </div>
      </div>
      <div className="flex flex-col bg-white">
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Masuk</h1>
            <p className="text-sm text-slate-500 mt-1">{module === "pribadi" ? "Masuk ke Keuangan Pribadi — kas, harta, utang & pajak pribadi (SPT 1770)." : "Masuk ke Keuangan Bisnis — Bisnis Online & Pabrik Arang."}</p>
          </div>
          <div className="grid grid-cols-2 p-1 bg-slate-100 rounded-sm" role="tablist" data-testid="login-module-tabs">
            {[{ k: "bisnis", l: "Bisnis" }, { k: "pribadi", l: "Pribadi" }].map((t) => (
              <button key={t.k} type="button" role="tab" aria-selected={module === t.k} onClick={() => setModule(t.k)}
                data-testid={`login-tab-${t.k}`}
                className={`py-2.5 text-sm font-medium rounded-sm transition-all ${module === t.k ? "bg-white shadow-sm text-[color:var(--brand)]" : "text-slate-500 hover:text-slate-800"}`}>
                {t.l}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="login-email" type="email" autoComplete="off" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="Masukkan email" required/>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" data-testid="login-password" type="password" autoComplete="new-password" value={password} onChange={(e)=>setPassword(e.target.value)} placeholder="Masukkan password" required/>
            <Captcha onToken={setCaptchaToken}/>
          </div>
          <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full brand-btn rounded-sm">
            {loading ? "Memuat..." : "Masuk"}
          </Button>
          {needVerify && (
            <div className="p-3 rounded-sm border border-amber-200 bg-amber-50 space-y-2" data-testid="login-need-verify">
              <p className="text-xs text-amber-800">Email <b>{needVerify}</b> belum diverifikasi.</p>
              <ResendVerification email={needVerify}/>
            </div>
          )}
          <div className="flex items-center justify-between text-sm">
            <Link to="/forgot-password" data-testid="forgot-link" className="text-[color:var(--brand)] hover:underline">Lupa Password?</Link>
            <Link to="/register" data-testid="register-link" className="text-[color:var(--brand)] font-medium hover:underline">Daftar Akun</Link>
          </div>
          <InstallAppButton variant="light"/>
        </form>
      </div>
      <AuthFooter/>
      </div>
    </div>
  );
}
